import { useState, useRef } from 'react';
import { Upload, X, FileImage } from 'lucide-react';

interface FileUploadProps {
  label: string;
  accept?: string;
  maxSizeMo?: number;
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  helperText?: string;
}

export default function FileUpload({
  label,
  accept = 'image/png,image/jpeg,image/svg+xml,application/pdf,image/webp',
  maxSizeMo = 10,
  onFileSelect,
  selectedFile,
  helperText,
}: FileUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxBytes = maxSizeMo * 1024 * 1024;
  const allowedTypes = accept.split(',');

  const validateFile = (file: File): boolean => {
    setError(null);

    if (file.size > maxBytes) {
      setError(`Le fichier dépasse ${maxSizeMo} Mo.`);
      return false;
    }

    if (!allowedTypes.some((type) => file.type === type.trim() || type.trim().includes(file.type))) {
      setError('Format non accepté (PNG, JPG, SVG, PDF, WEBP uniquement).');
      return false;
    }

    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && !validateFile(file)) {
      onFileSelect(null);
      return;
    }
    onFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0] ?? null;
    if (file && !validateFile(file)) {
      onFileSelect(null);
      return;
    }
    onFileSelect(file);
  };

  const clear = () => {
    onFileSelect(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>

      {selectedFile ? (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
          <FileImage className="w-8 h-8 text-primary-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{selectedFile.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{formatSize(selectedFile.size)}</p>
          </div>
          <button
            type="button"
            onClick={clear}
            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
            aria-label="Retirer le fichier"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 lg:p-8 text-center cursor-pointer transition-colors ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50/30'
          }`}
        >
          <Upload className="w-8 h-8 mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Cliquez ou glissez-déposez un fichier ici
          </p>
          {helperText && <p className="text-xs text-gray-400 mt-1">{helperText}</p>}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
      />

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
